import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from '@/contexts/ModalContext';
import { DeleteConfirmationDialog } from '~/global/components/delete-confirmation-dialog/DeleteConfirmationDialog';
import { DeleteConfirmationOptions } from './useDeleteConfirmation';

type Props = DeleteConfirmationOptions;

export function useDeleteProtection(
  onDelete: () => unknown,
  props?: Props,
): {
  dispatchConfirmationDialog(): void;
};
export function useDeleteProtection<P1>(
  onDelete: (p1: P1) => unknown,
  props?: Props,
): {
  dispatchConfirmationDialog(p1: P1): void;
};
export function useDeleteProtection<P1, P2>(
  onDelete: (p1: P1, p2: P2) => unknown,
  props?: Props,
): {
  dispatchConfirmationDialog(p1: P1, p2: P2): void;
};
export function useDeleteProtection(
  onDelete: (...args: unknown[]) => unknown,
  props?: Props,
) {
  const { showModal } = useModal();
  const { t } = useTranslation('common');

  const dispatchConfirmationDialog = useCallback(
    (...args: unknown[]) => {
      // Use common strings as defaults if no props provided
      const options: DeleteConfirmationOptions = {
        title: props?.title || t('deleteConfirmation.title'),
        description: props?.description || t('deleteConfirmation.description'),
        confirmText: props?.confirmText || t('deleteConfirmation.confirm'),
        cancelText: props?.cancelText || t('deleteConfirmation.cancel'),
        ...props, // Allow overriding with custom values
      };

      showModal(DeleteConfirmationDialog, {
        onConfirm: () => onDelete(...args),
        options,
      });
    },
    [onDelete, props, showModal, t],
  );

  return {
    dispatchConfirmationDialog,
  };
}
